import { FreeTrialStatus } from '@repo/subscription/api';
import {
  useSubscriptionStore,
  subscriptionStoreSelector,
} from '@repo/subscription/client';

import { useFreeTrialFeature } from '../global/growthbookUtils';
import { useUserStore, userStoreSelector } from '../stores/userStore';

type FreeTrialRunningData =
  | { isRunning: true; trialEndDate: Date }
  | { isRunning: false; trialEndDate: Date | null };

// the subscription is running if active or cancelled but not expired
export type FreeTrialData = {
  isAvailable: boolean;
  isCancelled: boolean;
  isPaidByKittl: boolean; // special case for subscription (PRO / EXPERT) granted via CMS
} & FreeTrialRunningData;

const DEFAULT_FREE_TRIAL_DATA: FreeTrialData = {
  isAvailable: false,
  isCancelled: false,
  isRunning: false,
  isPaidByKittl: false,
  trialEndDate: null,
};

const useFreeTrial = (): FreeTrialData => {
  const user = useUserStore(userStoreSelector.user);
  const isTeam = useUserStore(userStoreSelector.isTeam);
  const subscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );
  const freeTrialEnabled = useFreeTrialFeature();

  // if user is logged out, respect feature flag value
  if (user === null) {
    return {
      ...DEFAULT_FREE_TRIAL_DATA,
      isAvailable: freeTrialEnabled,
    };
  }

  if (isTeam) {
    return DEFAULT_FREE_TRIAL_DATA;
  } else if (!subscription || typeof subscription.freeTrial === 'boolean') {
    return {
      ...DEFAULT_FREE_TRIAL_DATA,
      isPaidByKittl: !!subscription?.freeTrial,
    };
  }

  const { freeTrial } = subscription;

  const trialEndDate = freeTrial?.expiresAt
    ? new Date(freeTrial.expiresAt)
    : null;
  const freeTrialRunningData: FreeTrialRunningData =
    freeTrial.status === FreeTrialStatus.RUNNING && trialEndDate
      ? { isRunning: true, trialEndDate }
      : { isRunning: false, trialEndDate };

  const freeTrialData: FreeTrialData = {
    isAvailable: freeTrial.status === FreeTrialStatus.AVAILABLE,
    isCancelled: !!freeTrial.cancelledAt,
    isPaidByKittl: freeTrial.status === FreeTrialStatus.PAID_BY_KITTL,
    ...freeTrialRunningData,
  };

  return freeTrialEnabled
    ? freeTrialData
    : {
        ...DEFAULT_FREE_TRIAL_DATA,
        isPaidByKittl: freeTrialData.isPaidByKittl,
      };
};

export default useFreeTrial;
